import { z } from "zod";

// Minting a catalog row from inside the template form is not a form submit:
// the page is already one <form>, so the dialog holds plain inputs and calls
// its action directly with a typed object. That leaves the JSON bodies as the
// right schemas — they take real values and coerce nothing — and this file as
// the counterpart to `parseFormData` for callers that never see FormData.

export type InlineCreateResult<TChoice> =
  | { wasCreated: true; choice: TChoice }
  // Keyed the same way `FormState.fieldErrors` is, so the dialog renders
  // errors from a failed parse and from a refused write identically.
  | { wasCreated: false; fieldErrors: Record<string, string[] | undefined> };

export const inlineFieldError = <TChoice>(
  field: string,
  message: string,
): InlineCreateResult<TChoice> => ({
  wasCreated: false,
  fieldErrors: { [field]: [message] },
});

export type InlineParseResult<TValue> =
  | { wasParsed: true; value: TValue }
  | { wasParsed: false; fieldErrors: Record<string, string[] | undefined> };

// A server action's arguments arrive from the client and are as untrusted as a
// request body, so the schema runs here rather than on the dialog's own state.
export const parseInline = <TSchema extends z.ZodObject>(
  schema: TSchema,
  input: unknown,
): InlineParseResult<z.infer<TSchema>> => {
  const parsed = schema.safeParse(input);

  return parsed.success
    ? { wasParsed: true, value: parsed.data }
    : {
        wasParsed: false,
        fieldErrors: z.flattenError(parsed.error).fieldErrors,
      };
};
